import { type History } from "history";
import { Component } from "react";
import {
    generatePath,
    type match,
    Route,
    type RouteComponentProps,
    withRouter,
} from "react-router";

import { add, removeAt, replaceAt } from "utilities/array/array";

// Type for entities that may have children
type EntityWithOptionalChildren<TEntity> = TEntity & {
    children?: TEntity[];
};

/**
 * This interface can be used or extended by your details page to drive what happens when an entity in a collection is added, updated,
 * or deleted and also provides a way to navigate back.  Use the following callbacks to preform the desired operation on the collection.
 *
 * @example
 * export class MyDetails extends React.Component<CollectionDetail<MyEntity>> {}
 *
 * Then in ICollectionEditableProps.renderDetail:
 *     renderDetail={detailProps => {
 *         <BTModal>
 *             <MyDetails {...detailProps} />
 *         </BTModal>
 *     }}
 */
export interface ICollectionDetail<TEntity> {
    /**
     * Callback to trigger when adding a new entity to the collection, normally you trigger this after your form submit api call
     * @example
     * <BTButton type="primary" onClick={() => {
     *      handler.save(myEntity);
     *      add(myEntity);
     *      navigateBack();
     *  }}>Save My Form</BTButton>
     */
    add: (newEntity: TEntity) => void;

    /**
     * Callback to trigger when udpating an entity in the collection, normally you trigger this after your form submit api call
     * @example
     * <BTButton type="primary" onClick={() => {
     *      handler.save(myEntity);
     *      edit(myEntity);
     *      navigateBack();
     *  }}>Save My Form</BTButton>
     */
    edit: (updatedEntity: TEntity) => void;

    /**
     * Callback to trigger when deleting an entity in the collection, normally you trigger this after your form submit api call
     * @example
     * <BTButton type="primary" onClick={() => {
     *      handler.delete(myEntity.id);
     *      delete(myEntity);
     *      navigateBackRemove();
     *  }}>Delete</BTButton>
     */
    delete: (removedEntity: TEntity) => void;

    /**
     * Callback to use to navigate back to the parent screen.
     * @example
     * <BTModal
     *     beforeClose={navigateBack}
     *     ...
     * >
     * </BTModal>
     */
    navigateBack: () => void;

    /**
     * Callback to use to navigate back to the parent screen and remove the previous item from history
     */
    navigateBackForDelete: () => void;
}

type RouteMatch = match<{ [key: string]: string | undefined }>;

export interface IDetailProps<TEntity> extends ICollectionDetail<TEntity> {
    /**
     * match object from the newly rendered route
     */
    match: RouteMatch;
    /**
     * history api for navigation
     */
    history: History;

    routeProps: RouteComponentProps;
    parentRouteProps: RouteComponentProps;
}

type DetailPathGenerator = string | ((match: RouteMatch) => string);
interface ICollectionEditableProps<TEntity, TId extends keyof TEntity> {
    /**
     * Property that uniquely identifies your entity
     *
     * @example use 'id' for an object shaped like this: { id: number, value: string }
     */
    idField: TId;
    /**
     * The collection that is being updated on
     * @example
     * collection={this.state.myCollection}
     */
    collection: TEntity[];
    /**
     * Function to propagate the edited collection up, normally this should be propagated up to the parent that is storing the collection in state.
     *
     * @param newCollection the newly edited collection
     * @param changedEntity the entity that was added/updated/deleted.
     *
     * @example
     * onValuesChange={(newCollection) => this.setState({ myCollection: newCollection })}
     */
    onValuesChange: (newCollection: TEntity[], changedEntity: TEntity) => void;
    /**
     * Renders the details page for adding/editing a collection item
     * @example
     * renderDetail={props => {
     *     <BTModal>
     *         {{Insert add/edit/delete form here!!!}}
     *     </BTModal>
     * }}>
     */
    renderDetail: (props: IDetailProps<TEntity>) => JSX.Element;
    /**
     * Path or function which returns a path that will be navigated to, to load your details component.
     *
     * THIS PATH IS RELATIVE TO THE PARENT COMPONENT.
     * @example (The parent page url = '/parent/1/2')
     * detailPath="/child/:id"
     */
    detailPath: DetailPathGenerator;

    children?: ((detailParams: IDisplayProps) => React.ReactNode) | JSX.Element | JSX.Element[];
}

// defines the parameter required by generatePath in react router.
interface IGeneratePathObject {
    [paramName: string]: string | number | boolean | undefined;
}

export interface IDisplayProps {
    getDetailPath: (data: IGeneratePathObject) => string;
}

class CollectionEditablePresentational<TEntity, TId extends keyof TEntity> extends Component<
    ICollectionEditableProps<TEntity, TId> & RouteComponentProps
> {
    private onAdd = (newEntity: TEntity) => {
        this.props.onValuesChange(add(this.props.collection, newEntity), newEntity);
    };

    /**
     * Searches for an edited or deleted entity in a collection and returns indices of where it was found.
     * - If the updated entity is found as a child of another entity, then two indices wlil be returned:
     *     - "childIndex" for where in the list of children the updated entity was found
     *     - "index" for where in the list that list of children was found.
     * - If the updated entity was found in the root list
     *     - the index that the updated entity was found will be assigned to "index"
     *     - "childIndex" will be set to -1.
     * - If the updated entity could not be found in any lists or in the children lists
     *     - "index" and "childIndex" will both returned as -1
     * @param updatedEntity The entity from the collection that is either being edited or deleted.
     */
    private getUpdatedElementIndices = (
        updatedEntity: TEntity,
    ): { index: number; childIndex: number } => {
        const collection = [...this.props.collection];
        for (let index = 0; index < collection.length; index++) {
            const v = collection[index] as EntityWithOptionalChildren<TEntity>;
            if (v.children) {
                const childIndex = v.children.findIndex(
                    (child: TEntity) =>
                        child[this.props.idField] === updatedEntity[this.props.idField],
                );
                if (childIndex >= 0) {
                    return { index: index, childIndex: childIndex };
                }
            } else {
                if (v[this.props.idField] === updatedEntity[this.props.idField]) {
                    return { index: index, childIndex: -1 };
                }
            }
        }
        return { index: -1, childIndex: -1 };
    };
    private onEdit = (updatedEntity: TEntity) => {
        const collection = [...this.props.collection];
        const { index, childIndex } = this.getUpdatedElementIndices(updatedEntity);
        if (childIndex >= 0) {
            const parentEntity = collection[index] as EntityWithOptionalChildren<TEntity>;
            if (parentEntity.children) {
                parentEntity.children.splice(childIndex, 1, updatedEntity);
            }
            collection.splice(index, 1, collection[index]);
            this.props.onValuesChange(collection, updatedEntity);
        } else if (index >= 0) {
            this.props.onValuesChange(replaceAt(collection, updatedEntity, index), updatedEntity);
        }
    };
    private onDelete = (deletedEntity: TEntity) => {
        const collection = [...this.props.collection];
        const { index, childIndex } = this.getUpdatedElementIndices(deletedEntity);
        if (childIndex >= 0) {
            const parentEntity = collection[index] as EntityWithOptionalChildren<TEntity>;
            // Remove the child element from the list of children
            if (parentEntity.children) {
                parentEntity.children.splice(childIndex, 1);
                // If the list of children is empty...
                if (parentEntity.children.length === 0) {
                    // then remove the list
                    collection.splice(index, 1);
                }
            }
            this.props.onValuesChange(collection, deletedEntity);
        } else if (index >= 0) {
            this.props.onValuesChange(removeAt(collection, index), deletedEntity);
        }
    };

    private navigateBack = () => {
        this.props.history.goBack();
    };

    private navigateBackForDelete = () => {
        const search = this.props.location.search;
        this.props.history.replace(this.props.match.url + (search ? search : ""));
    };

    public render() {
        const routePath = getDetailPath(this.props.match, this.props.detailPath);
        const generatedPath = getDetailPath(
            this.props.match,
            this.props.detailPath,
            this.props.location.search,
        );

        let children: React.ReactNode;
        if (typeof this.props.children === "function") {
            children = this.props.children({
                getDetailPath: (data: IGeneratePathObject) => generatePath(generatedPath, data),
            });
        } else {
            children = this.props.children;
        }

        return (
            <>
                {children}
                <Route
                    path={routePath}
                    render={(routeProps) =>
                        this.props.renderDetail({
                            add: this.onAdd,
                            edit: this.onEdit,
                            delete: this.onDelete,
                            navigateBack: this.navigateBack,
                            navigateBackForDelete: this.navigateBackForDelete,
                            match: routeProps.match,
                            history: routeProps.history,
                            routeProps: routeProps,
                            parentRouteProps: {
                                history: this.props.history,
                                location: this.props.location,
                                match: this.props.match,
                            },
                        })
                    }
                />
            </>
        );
    }
}

export const CollectionEditable: new <TEntity, TId extends keyof TEntity>(
    props: ICollectionEditableProps<TEntity, TId>,
) => React.Component<ICollectionEditableProps<TEntity, TId>> = withRouter(
    CollectionEditablePresentational as any,
) as any;

export const getDetailPath = (
    match: RouteMatch,
    detailPath: DetailPathGenerator,
    search?: string,
) => {
    const path = typeof detailPath === "string" ? detailPath : detailPath(match);
    return match.url + path + (search ? search : "");
};
